// File: frontend/src/pages/InvestorPitch/components/slides/OperationalModelSlide/OperationalModelSlide.js

import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
  BarChart, Bar, Cell
} from 'recharts';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { ANIMATION_VARIANTS } from '../../../constants/animations';
import styles from './OperationalModelSlide.module.css';

const CardComponent = ({ title, children }) => {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{title}</h3>
      </div>
      <div className={styles.cardContent}>
        {children}
      </div>
    </div>
  );
};

const DISTRIBUTION_PARAMS = {
  TOTAL_EXPENSE_AMOUNT: 1074e6,
  TOLERANCE_PERCENTAGE: 0.005,

  PEAK_1_AMOUNT: 2500,
  PEAK_1_PROPORTION: 0.30,
  PEAK_1_SPREAD: 0.4,

  PEAK_2_AMOUNT: 10000,
  PEAK_2_PROPORTION: 0.30,
  PEAK_2_SPREAD: 0.45,

  PEAK_3_AMOUNT: 30000,
  PEAK_3_PROPORTION: 0.20,
  PEAK_3_SPREAD: 0.5,

  TAIL_THRESHOLD: 100000,
  TAIL_PROPORTION: 0.20,
  TAIL_SPREAD: 500000,
  TAIL_SHAPE: 0.3,

  AI_IMPACTS: {
    SEGMENT_1: { REDUCTION: 0.999, COLOR: 'var(--color-blue-210)' },
    SEGMENT_2: { REDUCTION: 0.80, COLOR: 'var(--color-blue-310)' },
    SEGMENT_3: { REDUCTION: 0.75, COLOR: 'var(--color-blue-410)' },
    SEGMENT_4: { REDUCTION: 0.50, COLOR: 'var(--color-blue-510)' }
  },

  MAX_X_VALUE: 1000000,
  GRANULARITY_LEVELS: {
    VERY_FINE: { threshold: 10000, step: 500 },
    FINE: { threshold: 50000, step: 1000 },
    MEDIUM: { threshold: 100000, step: 2500 },
    COARSE: { threshold: 25000000, step: 50000 }
  }
};

const formatCurrency = (value) => {
  if (value >= 1000000) {
    const millions = value / 1000000;
    return `$${millions < 10 ? millions.toFixed(1) : Math.round(millions)}M`;
  } else if (value >= 1000) {
    const thousands = value / 1000;
    return `$${thousands < 10 ? thousands.toFixed(1) : Math.round(thousands)}k`;
  }
  return `$${Math.round(value)}`;
};

const OperationalModelSlide = () => {
  const { t } = useLanguage();

  // Expense distribution data
  const expenseData = [
    {
      name: "Auto",
      Operational: 283,
      Acquisition: 690,
      IT: 101,
      color: "var(--color-blue-310)"
    },
    {
      name: "Home",
      Operational: 115,
      Acquisition: 288,
      IT: 43,
      color: "var(--color-green-310)"
    },
    {
      name: "Commercial",
      Operational: 53,
      Acquisition: 120,
      IT: 19,
      color: "var(--color-orange-310)"
    }
  ];

  // Mixture model function with safeguards
  const mixtureModel = useCallback((x, params, aiReduction = 0) => {
    if (x <= 0) return 0;
    const scale = 1 - aiReduction;

    try {
      // First peak (around $2.5K)
      const peak1 = DISTRIBUTION_PARAMS.PEAK_1_PROPORTION * 
                  (1/(x * DISTRIBUTION_PARAMS.PEAK_1_SPREAD * Math.sqrt(2*Math.PI))) * 
                  Math.exp(-Math.pow(Math.log(x) - Math.log(DISTRIBUTION_PARAMS.PEAK_1_AMOUNT), 2)/
                  (2 * Math.pow(DISTRIBUTION_PARAMS.PEAK_1_SPREAD, 2)));

      // Second peak (around $10K)
      const peak2 = DISTRIBUTION_PARAMS.PEAK_2_PROPORTION * 
                  (1/(x * DISTRIBUTION_PARAMS.PEAK_2_SPREAD * Math.sqrt(2*Math.PI))) * 
                  Math.exp(-Math.pow(Math.log(x) - Math.log(DISTRIBUTION_PARAMS.PEAK_2_AMOUNT), 2)/
                  (2 * Math.pow(DISTRIBUTION_PARAMS.PEAK_2_SPREAD, 2)));

      // Third peak (around $30K)
      const peak3 = DISTRIBUTION_PARAMS.PEAK_3_PROPORTION * 
                  (1/(x * DISTRIBUTION_PARAMS.PEAK_3_SPREAD * Math.sqrt(2*Math.PI))) * 
                  Math.exp(-Math.pow(Math.log(x) - Math.log(DISTRIBUTION_PARAMS.PEAK_3_AMOUNT), 2)/
                  (2 * Math.pow(DISTRIBUTION_PARAMS.PEAK_3_SPREAD, 2)));

      // Heavy tail component
      const tailComponent = x > DISTRIBUTION_PARAMS.TAIL_THRESHOLD ? 
                          DISTRIBUTION_PARAMS.TAIL_PROPORTION * (1/DISTRIBUTION_PARAMS.TAIL_SPREAD) * 
                          Math.pow(1 + (DISTRIBUTION_PARAMS.TAIL_SHAPE*(x-DISTRIBUTION_PARAMS.TAIL_THRESHOLD))/
                          DISTRIBUTION_PARAMS.TAIL_SPREAD, -(1/DISTRIBUTION_PARAMS.TAIL_SHAPE + 1)) : 0;

      const result = (peak1 + peak2 + peak3 + tailComponent) * scale;
      return isNaN(result) ? 0 : result;
    } catch (error) {
      console.error('Error in mixtureModel:', error);
      return 0;
    }
  }, []);

  // Calculate scaling factor
  const calculateScalingFactor = useCallback(() => {
    let totalArea = 0;
    let x = 100;

    while (x <= DISTRIBUTION_PARAMS.MAX_X_VALUE) {
      let dx;
      if (x < DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.VERY_FINE.threshold) {
        dx = DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.VERY_FINE.step;
      } else if (x < DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.FINE.threshold) {
        dx = DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.FINE.step;
      } else if (x < DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.MEDIUM.threshold) {
        dx = DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.MEDIUM.step;
      } else {
        dx = DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.COARSE.step;
      }

      const y = mixtureModel(x, null, 0);
      if (!isNaN(y)) {
        totalArea += y * x * dx;
      }
      x += dx;
    }

    return totalArea > 0 ? DISTRIBUTION_PARAMS.TOTAL_EXPENSE_AMOUNT / totalArea : 1;
  }, [mixtureModel]);

  const [scalingFactor] = useState(calculateScalingFactor());

  // Generate data points
  const generateData = useCallback(() => {
    const data = [];
    let x = DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.VERY_FINE.step;
    
    while (x <= DISTRIBUTION_PARAMS.MAX_X_VALUE) {
      const baselineFreq = mixtureModel(x, null, 0) * scalingFactor;
      
      const segment1Impact = baselineFreq * (DISTRIBUTION_PARAMS.AI_IMPACTS.SEGMENT_1.REDUCTION);
      const segment2Impact = baselineFreq * (DISTRIBUTION_PARAMS.AI_IMPACTS.SEGMENT_2.REDUCTION);
      const segment3Impact = baselineFreq * (DISTRIBUTION_PARAMS.AI_IMPACTS.SEGMENT_3.REDUCTION);
      const segment4Impact = baselineFreq * (DISTRIBUTION_PARAMS.AI_IMPACTS.SEGMENT_4.REDUCTION);

      if (!isNaN(baselineFreq)) {
        data.push({
          x,
          baseline: baselineFreq,
          aiImpact1: x <= DISTRIBUTION_PARAMS.PEAK_1_AMOUNT ? segment1Impact : 0,
          aiImpact2: (x > DISTRIBUTION_PARAMS.PEAK_1_AMOUNT && x <= DISTRIBUTION_PARAMS.PEAK_2_AMOUNT) ? segment2Impact : 0,
          aiImpact3: (x > DISTRIBUTION_PARAMS.PEAK_2_AMOUNT && x <= DISTRIBUTION_PARAMS.PEAK_3_AMOUNT) ? segment3Impact : 0,
          aiImpact4: x > DISTRIBUTION_PARAMS.PEAK_3_AMOUNT ? segment4Impact : 0
        });
      }
      
      let step;
      if (x < DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.VERY_FINE.threshold) {
        step = DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.VERY_FINE.step;
      } else if (x < DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.FINE.threshold) {
        step = DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.FINE.step;
      } else if (x < DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.MEDIUM.threshold) {
        step = DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.MEDIUM.step;
      } else {
        step = DISTRIBUTION_PARAMS.GRANULARITY_LEVELS.COARSE.step;
      }
      x += step;
    }
    
    return data;
  }, [mixtureModel, scalingFactor]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={styles.tooltip}>
          <p className={styles.tooltipLabel}>Cost: {formatCurrency(label)}</p>
          {payload.map((entry) => (
            <p key={entry.name} style={{ color: entry.color }}>
              {entry.name}: {Number(entry.value).toLocaleString(undefined, {
                maximumFractionDigits: 2
              })}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const ExpenseTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, p) => sum + (p.value || 0), 0);
      return (
        <div className={styles.tooltip}>
          <p className={styles.tooltipLabel}>{label}</p>
          {payload.map((p) => (
            <div key={p.name} className={styles.tooltipRow}>
              <span>{p.name}:</span>
              <span>${p.value}M</span>
              <span>{((p.value / total) * 100).toFixed(1)}%</span>
            </div>
          ))}
          <div className={styles.tooltipTotal}>
            <span>Total:</span>
            <span>${total}M</span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <motion.div
      className={styles.container}
      variants={ANIMATION_VARIANTS.stagger.container}
      initial="hidden"
      animate="visible"
    >
      <motion.div 
        className={styles.header}
        variants={ANIMATION_VARIANTS.fadeIn}
      >
        <h2 className={styles.title}>
          {t('investor_pitch.operational_model.title', 'AI-Powered Operational Model')}
        </h2>
        {/* <p className={styles.subtitle}>
          {t('investor_pitch.operational_model.subtitle', 'Combining actuarial science with LLMs to transform insurance operations')}
        </p> */}
      </motion.div>

      <div className={styles.content}>
        <motion.div 
          className={styles.chartSection}
          variants={ANIMATION_VARIANTS.fadeIn}
        >
          <div className={styles.chartWrapper}>
            <CardComponent title={t('investor_pitch.operational_model.expense_distribution', 'Traditional Insurance Expense Distribution')}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  data={expenseData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: 'var(--color-white)', fontSize: 14 }} 
                  />
                  <YAxis 
                    label={{ 
                      value: 'Million USD', 
                      angle: -90, 
                      position: 'insideLeft', 
                      fill: 'var(--color-gray-300)',
                      fontSize: 14
                    }} 
                    tick={{ fill: 'var(--color-white)', fontSize: 14 }} 
                  />
                  <Tooltip content={<ExpenseTooltip />} />
                  <Legend 
                    verticalAlign="bottom"
                    iconType="circle"
                    iconSize={10}
                    wrapperStyle={{
                      paddingTop: '20px',
                      fontSize: '14px',
                      color: 'var(--color-white)',
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '30px'
                    }}
                    formatter={(value) => (
                      <span style={{ color: 'var(--color-white)', fontSize: '14px', padding: '0 5px' }}>
                        {value}
                      </span>
                    )}
                  />
                  <Bar dataKey="Operational" stackId="a" fill="var(--color-blue-310)" />
                  <Bar dataKey="Acquisition" stackId="a" fill="var(--color-green-310)" />
                  <Bar dataKey="IT" stackId="a" fill="var(--color-orange-310)" />
                </BarChart>
              </ResponsiveContainer>
            </CardComponent>
          </div>

          <div className={styles.chartWrapper}>
            <CardComponent title={t('investor_pitch.operational_model.expense_modeling', 'Expense Operations Modeling')}>
              <ResponsiveContainer width="100%" height={400}>
                <ComposedChart
                  data={generateData()}
                  margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
                  background="transparent"
                >
                  <CartesianGrid 
                    strokeDasharray="3 3" 
                    stroke="rgba(255, 255, 255, 0.1)"
                    vertical={false}
                  />
                  {[
                    DISTRIBUTION_PARAMS.PEAK_1_AMOUNT,
                    DISTRIBUTION_PARAMS.PEAK_2_AMOUNT,
                    DISTRIBUTION_PARAMS.PEAK_3_AMOUNT
                  ].map((peak) => (
                    <ReferenceLine
                      key={peak}
                      x={peak}
                      stroke="rgba(255, 255, 255, 0.2)"
                      strokeDasharray="3 3"
                      label={{
                        value: formatCurrency(peak),
                        position: 'top',
                        fill: 'var(--color-gray-300)',
                        fontSize: 14,
                        fontWeight: 500
                      }}
                    />
                  ))}
                  <XAxis
                    dataKey="x"
                    scale="log"
                    domain={['auto', 'auto']}
                    tickFormatter={formatCurrency}
                    stroke="var(--color-gray-300)"
                    tick={{ fill: 'var(--color-gray-300)', fontSize: 14 }}
                    axisLine={{ stroke: 'var(--color-gray-600)' }}
                    tickLine={{ stroke: 'var(--color-gray-600)' }}
                    label={{
                      value: 'Expense Amount in US Dollars',
                      position: 'bottom',
                      offset: 10,
                      style: {
                        fill: 'var(--color-gray-300)',
                        fontSize: 14,
                        fontWeight: 500
                      }
                    }}
                  />
                  <YAxis
                    stroke="var(--color-gray-300)"
                    tick={{ fill: 'var(--color-gray-300)', fontSize: 14 }}
                    axisLine={{ stroke: 'var(--color-gray-600)' }}
                    tickLine={{ stroke: 'var(--color-gray-600)' }}
                    label={{
                      value: 'Frequency',
                      angle: -90,
                      position: 'insideLeft',
                      offset: -5,
                      style: {
                        fill: 'var(--color-gray-300)',
                        fontSize: 14,
                        fontWeight: 500
                      }
                    }}
                  />
                  <Tooltip 
                    content={<CustomTooltip />}
                    cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                  />
                  <Legend 
                    verticalAlign="bottom"
                    iconType="circle"
                    iconSize={10}
                    wrapperStyle={{
                      paddingTop: '20px',
                      fontSize: '14px',
                      color: 'var(--color-white)',
                      display: 'flex',
                      justifyContent: 'center',
                      gap: '30px',
                      flexWrap: 'wrap'
                    }}
                    formatter={(value) => (
                      <span style={{ color: 'var(--color-white)', fontSize: '14px', padding: '0 5px' }}>
                        {value}
                      </span>
                    )}
                  />
                  <Line
                    type="monotone"
                    dataKey="baseline"
                    stroke="var(--color-red-310)"
                    name="OPEX Distribution"
                    dot={false}
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="aiImpact1"
                    fill="var(--color-blue-210)"
                    stroke="none"
                    fillOpacity={0.4}
                    name="Simple Operations"
                  />
                  <Area
                    type="monotone"
                    dataKey="aiImpact2"
                    fill="var(--color-blue-310)"
                    stroke="none"
                    fillOpacity={0.4}
                    name="Medium Complexity"
                  />
                  <Area
                    type="monotone"
                    dataKey="aiImpact3"
                    fill="var(--color-blue-410)"
                    stroke="none"
                    fillOpacity={0.4}
                    name="High Complexity"
                  />
                  <Area
                    type="monotone"
                    dataKey="aiImpact4"
                    fill="var(--color-blue-510)"
                    stroke="none"
                    fillOpacity={0.4}
                    name="Strategic Operations"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardComponent>
          </div>
        </motion.div>

        <motion.div 
          className={styles.infoSection}
          variants={ANIMATION_VARIANTS.stagger.container}
        >
          <motion.div 
            className={styles.infoCardWrapper}
            variants={ANIMATION_VARIANTS.stagger.item}
          >
            <CardComponent title={t('investor_pitch.operational_model.comprehensive_modeling', 'Comprehensive Modeling')}>
              <p className={styles.infoText}>
                {t('investor_pitch.operational_model.modeling_description', 'We\'ve modeled all expense-bearing operations from 1¢ to $25M for a $3.2B carrier, combining actuarial science with LLMs to build a custom infrastructure that automates workflows across four complexity categories.')}
              </p>
            </CardComponent>
          </motion.div>

          <motion.div 
            className={styles.statsWrapper}
            variants={ANIMATION_VARIANTS.stagger.item}
          >
            <CardComponent title={t('investor_pitch.operational_model.expense_reduction', 'Expense Reduction')}>
              <div className={styles.statsContainer}>
                <div className={styles.statItem}>
                  <div className={styles.statValue}>99.9%</div>
                  <div className={styles.statLabel}>{t('investor_pitch.operational_model.underwriting_reduction', 'Underwriting & Back-Office Reduction')}</div>
                </div>
                <div className={styles.statItem}>
                  <div className={styles.statValue}>80%</div>
                  <div className={styles.statLabel}>{t('investor_pitch.operational_model.acquisition_reduction', 'Customer Acquisition Reduction')}</div>
                </div>
                <div className={styles.statItem}>
                  <div className={styles.statValue}>75%</div>
                  <div className={styles.statLabel}>{t('investor_pitch.operational_model.it_reduction', 'IT Expenses Reduction')}</div>
                </div>
              </div>
            </CardComponent>
          </motion.div>

          <motion.div 
            className={styles.infoCardWrapper}
            variants={ANIMATION_VARIANTS.stagger.item}
          >
            <CardComponent title={t('investor_pitch.operational_model.rapid_scaling', 'Rapid Scaling')}>
              <p className={styles.infoText}>
                {t('investor_pitch.operational_model.scaling_description', 'Since our platform is built with AI-assisted tools, scaling it vertically or horizontally takes days instead of months or years, fundamentally transforming insurance operational economics.')}
              </p>
            </CardComponent>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default OperationalModelSlide;